/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React, { useContext, useMemo, useState } from 'react';
import { Button, Card, Divider } from '@blueprintjs/core';
import { ChartDropdown } from 'components/dropdowns/chartDropdown';
import { WIDGETS } from 'widgets';
import { SaveWidget } from 'views/save/saveCurrentView';
import { ErrorBoundary } from 'components/errorBoundary';
import { ExplorerCard } from 'views/explorer/explorerCard';
import SettingsDialog from 'views/explorer/settingsDialog';
import { WorkspaceContext } from 'views/workspace/workspaceContext';
import { useIsSavedStateAvailable } from 'hooks/useApplicationState';
import { LAYOUT_OPTIONS } from 'views/explorer/layoutOptions';
import { WorkspaceActionCreator } from 'hooks/useWorkspaceState';
import CardContext from './cardContext';
import { SavedWidgetSelector } from 'components/selectors/savedViewSelector';
import { FlexSplitter } from 'styles/styledComponentUtils';

const { updateExplorerCardWidget, maximizeWidget } = WorkspaceActionCreator;

export default function AppCard({ card, cardIndex, availableWidgets, layoutOptions = undefined, showAllSavedWidgets = false, hideSave = false, hideHeader = false }) {
	const {
		state: {
			explorer: { layout, selectedDefinitionName },
			transient: { activeResult },
		},
		dispatch,
	} = useContext(WorkspaceContext);
	const isSavable = useIsSavedStateAvailable();

	const isSettingsAvailable = card?.widgetConfiguration?.showSettings ?? true;
	const [isSettingsOpen, setSettingsOpenStatus] = useState(false);

	const handleReset = () => dispatch(updateExplorerCardWidget(cardIndex, card.type));
	const layoutStyle = layoutOptions ? layoutOptions[layout.layoutStyle.key] : LAYOUT_OPTIONS[layout.layoutStyle.key];

	const toggleSettingsDialog = () => {
		setSettingsOpenStatus((d) => !d);
	};

	const value = useMemo(
		() => ({
			isSettingsDialogOpen: isSettingsOpen,
		}),
		[isSettingsOpen]
	);

	const widgetUnavailable = useMemo(() => {
		return !availableWidgets.filter(x => x.key === card.type).length > 0;
	}, [card, availableWidgets]);

	return (
		<CardContext.Provider value={value}>
			<Card cy-test="widget-card" css={cardStyle}>
				{!hideHeader && 
					<div css={controlsStyle}>
						<ChartDropdown
							onSelectionChanged={(widget) => dispatch(updateExplorerCardWidget(cardIndex, widget.key))}
							availableWidgets={availableWidgets}
							value={WIDGETS[card.type]}
						/>
						<FlexSplitter />
						{isSettingsAvailable && (
							<Button
								cy-test="show-settings-button"
								minimal
								onClick={toggleSettingsDialog}
								icon={'settings'}
								children={'Settings'}
							/>
						)}
						<Button cy-test="reset-button" minimal onClick={handleReset} icon={'reset'} />
						{isSavable && !hideSave && (
							<React.Fragment>
								<SaveWidget card={card} />
								<SavedWidgetSelector cardIndex={cardIndex} showAllSavedWidgets={showAllSavedWidgets} />
							</React.Fragment>
						)}
						{layoutStyle.cardCount > 1 && (
							<Button minimal onClick={() => dispatch(maximizeWidget(cardIndex))} icon={'maximize'} />
						)}
					</div>
				}
				<Divider css={dividerStyle} />

				{/* Property selector Error states are handled by this boundary */}
				<ErrorBoundary
					title={'Whoops!'}
					description={'An error occurred within this widget. Reload the widget or refresh the page.'}
					retryButtonText={'Reload'}
					onReset={handleReset}
				>
					{widgetUnavailable ? (
						<Card>Widget Unavailable</Card>
						) : (
							<ExplorerCard
								dispatch={dispatch}
								cardIndex={cardIndex}
								card={card}
								selectedDefinitionName={selectedDefinitionName}
								result={activeResult}
							/>
						)
					}
				</ErrorBoundary>
				<SettingsDialog isOpen={isSettingsOpen} close={toggleSettingsDialog} card={card} cardIndex={cardIndex} />
			</Card>
		</CardContext.Provider>
	);
}

const controlsStyle = css`
	flex: 0 0 auto;
	display: flex;
	margin: 0 -7px;
	padding: 0.25rem;
`;

const dividerStyle = css`
	margin: 0 -2rem 0.5rem;
`;

export const cardStyle = css`
	padding: 0 0.75rem 0.75rem 0.75rem;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;
